import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import callBackendAPI, { callAPI } from '../../../utils/callBackendAPI';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Modal, Row, Col } from 'react-bootstrap';
import { ErrorMessage, Field, Formik, Form, useFormikContext } from 'formik';
import { IoClose } from 'react-icons/io5';
import { signUpValidateSchema } from '../../../utils/yupSchemas/ResgistrationSchema';
import Busy from '../../../assets/gif/busy.gif';
import './SignupForm.scss';
import { toast } from 'react-toastify';
import { toastDefaults } from '../../../utils/ToastOptionCommon';
import FormikFormPasswordField from '../../../utils/FormikPasswordField';
import FormikFormInput from '../../../utils/FormikFormInput';
import FormikFormSelectField from '../../../utils/FormikFormSelect';
import { useStateCity } from '../../../hooks/useStateCity';
import { Base64 } from 'js-base64';
import { trackEvent } from "../../../utils/Analytics";

interface IProps {
  submitHandler: (data: any) => void;
  onModalClosedSignUp: () => void;
  onModalClosedSignIn: () => void;
  initialValues: ISignUp;
  isLoading: boolean;
  setLoginError: Dispatch<SetStateAction<string>>;
  loginError: string;
  passwordError: string;
  setPasswordError: Dispatch<SetStateAction<string>>;
  selectedPlanId?: string | null;
}

export interface ISignUp {
  FirstName?: string;
  LastName?: string;
  Email: string;
  Password: string;
  PhoneNo: string;
  ConfirmPassword: string;
  Role: string;
  UserType: string;
  TermsConditions: boolean;
  City: { value: string; label: string };
  State: { value: string; label: string };
  Gender: string;
}

const SignupForm = (props: IProps) => {
  const {
    submitHandler,
    onModalClosedSignUp,
    onModalClosedSignIn,
    initialValues,
    isLoading,
    loginError,
    setLoginError,
    passwordError,
    setPasswordError,
    selectedPlanId,
  } = props;

  const [isProgress, setIsProgress] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [state, setState] = useState('');
  const [allCity, setAllCity] = useState<any>([]);
  const [allState, setAllState] = useState<any>([]);
  const { loading, states, stateLoading, cities } = useStateCity({
    stateCode: state,
  });

  const genderOption = [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
  ];

  const roleOption = [
    { label: 'Owner', value: 'Owner' },
    { label: 'Tenant', value: 'Tenant' },
  ];

  trackEvent("signup_popup_open");

  useEffect(() => {
    if (states.length) {
      const stateList = states?.map((e) => ({ value: e.code, label: e.name }));
      setAllState(stateList);
    }
  }, [states]);

  useEffect(() => {
    if (cities.length) {
      const cityList = cities?.map((e, i) => ({
        value: `${e.code}_${i}`,
        label: e.name,
      }));
      setAllCity(cityList);
    }
  }, [cities]);

  const handleSubmit = async (values: ISignUp) => {
    setLoginError(() => '');
    setPasswordError(() => '');
    setIsProgress(() => true);
    try {
      const response = await callAPI('POST', 'auth/register', null, {
        userEmail: values.Email,
        firstName: values.FirstName,
        lastName: values.LastName,
        password: Base64.encode(values.Password),
        city: values.City.label,
        state: values.State,
        phoneNo: values.PhoneNo,
        gender: values.Gender,
        role: values.Role,
      });

      if (response.error) {
        setIsProgress(() => false);
        toast.error(response.error.toString(), toastDefaults());
      } else {
        setIsProgress(() => false);
        toast.success('Sign up successfully', toastDefaults());
        handleSignInRoute();
      }
    } catch (error: any) {
      setIsProgress(() => false);
      if (error?.response?.status === 409) {
        toast.error('Email already exists', toastDefaults());
      } else {
        toast.error('Sign Up failed:', toastDefaults());
      }
    }
  };

  const handleSignInRoute = () => {
    onModalClosedSignUp();
    onModalClosedSignIn();
  };

  const FormValue = () => {
    const { values, setFieldValue } = useFormikContext<any>();
    useEffect(() => {
      if (values.State.value !== '') {
        setState(values.State.value);
        setFieldValue('City', { value: '', label: 'Select City' });
      }
    }, [values.State]);

    return null;
  };

  return (
    <Modal className="auth-modal" show={true} centered>
      <Modal.Header>
        <div className="modal-header-content">
          <Modal.Title>Create your account</Modal.Title>
          <p className="modal-subtitle">
            Join us to manage or find your ideal property
          </p>
        </div>
        <div
          onClick={onModalClosedSignUp}
          className="icons8-close ms-auto"
          aria-label="Close"
        >
          <IoClose />
        </div>
      </Modal.Header>

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={signUpValidateSchema}
        onSubmit={handleSubmit}
      >
        {({ touched, errors, values }) => (
          <Form noValidate>
            <FormValue />
            <Modal.Body>
              {loginError && (
                <div className="alert alert-danger mb-3">{loginError}</div>
              )}

              {/* User Type Selection */}
              <div className="form-group mb-3">
                <label className="form-label">I am signing up as</label>
                <div className="radio-group-segmented">
                  {roleOption.map((option) => (
                    <label
                      key={option.value}
                      className={`radio-tile ${values.Role.toLowerCase() === option.value.toLowerCase() ? 'selected' : ''}`}
                    >
                      <Field type="radio" name="Role" value={option.value} />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
                <ErrorMessage
                  component="div"
                  name="Role"
                  className="invalid-feedback d-block"
                />
              </div>

              {/* Name Row */}
              <Row className="g-2 mb-2">
                <Col md={6}>
                  <label htmlFor="firstName" className="form-label">
                    First Name
                  </label>
                  <Field
                    id="firstName"
                    name="FirstName"
                    type="text"
                    className="form-control"
                    placeholder="John"
                  />
                  <ErrorMessage
                    component="div"
                    name="FirstName"
                    className="invalid-feedback d-block"
                  />
                </Col>
                <Col md={6}>
                  <label htmlFor="lastName" className="form-label">
                    Last Name
                  </label>
                  <Field
                    id="lastName"
                    name="LastName"
                    type="text"
                    className="form-control"
                    placeholder="Doe"
                  />
                  <ErrorMessage
                    component="div"
                    name="LastName"
                    className="invalid-feedback d-block"
                  />
                </Col>
              </Row>

              {/* Email */}
              <div className="mb-2">
                <label htmlFor="email" className="form-label">
                  Email Address
                </label>
                <Field
                  id="email"
                  name="Email"
                  type="email"
                  className="form-control"
                  placeholder="name@example.com"
                />
                <ErrorMessage
                  component="div"
                  name="Email"
                  className="invalid-feedback d-block"
                />
              </div>

              {/* Password & Phone Row */}
              <Row className="g-2 mb-2">
                <Col md={6}>
                  <label htmlFor="password" className="form-label">
                    Password
                  </label>
                  <Field
                    id="password"
                    name="Password"
                    type={showPassword ? 'text' : 'password'}
                    className="form-control"
                    as={FormikFormPasswordField}
                  />
                  <ErrorMessage
                    component="div"
                    name="Password"
                    className="invalid-feedback d-block"
                  />
                </Col>
                <Col md={6}>
                  <label htmlFor="phoneno" className="form-label">
                    Phone Number
                  </label>
                  <Field
                    id="phoneno"
                    name="PhoneNo"
                    className="form-control"
                    placeholder="9876543210"
                    regexp={/^[0-9]*$/}
                    as={FormikFormInput}
                  />
                  <ErrorMessage
                    component="div"
                    name="PhoneNo"
                    className="invalid-feedback d-block"
                  />
                </Col>
              </Row>

              {/* State & City Row */}
              <Row className="g-3 mb-3">
                <Col md={6}>
                  <label className="form-label" htmlFor="state">
                    State
                  </label>
                  <div className="select-field-wrapper">
                    <Field
                      as={FormikFormSelectField}
                      isLabelValueSame={true}
                      id="state"
                      name="State"
                      className={`form-select ${touched.State && errors.State ? 'is-invalid' : ''}`}
                      options={allState}
                      loading={stateLoading}
                    />
                    <ErrorMessage
                      name="State"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>
                </Col>

                <Col md={6}>
                  <label className="form-label" htmlFor="city">
                    City
                  </label>
                  <div className="select-field-wrapper">
                    <Field
                      as={FormikFormSelectField}
                      isLabelValueSame={true}
                      id="city"
                      name="City"
                      className={`form-select ${touched.City && errors.City ? 'is-invalid' : ''}`}
                      options={allCity}
                      loading={loading}
                    />
                    <ErrorMessage
                      name="City"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>
                </Col>
              </Row>

              {/* Gender Pill Selection */}
              <div className="mb-3">
                <label className="form-label">Gender</label>
                <div className="d-flex gap-2">
                  {genderOption.map((option) => (
                    <label
                      key={option.value}
                      className={`gender-pill ${values.Gender === option.value ? 'selected' : ''}`}
                    >
                      <Field type="radio" name="Gender" value={option.value} />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Terms Checkbox */}
              <div className="form-check my-3">
                <Field
                  id="terms"
                  name="TermsConditions"
                  type="checkbox"
                  className="form-check-input"
                />
                <label htmlFor="terms" className="form-check-label ms-1">
                  I have read and accept the{' '}
                  <Link to="/terms-and-conditions" target="_blank">
                    Terms & Privacy Policy
                  </Link>
                </label>
              </div>
              <ErrorMessage
                component="div"
                name="TermsConditions"
                className="invalid-feedback d-block mb-2"
              />

              {/* Submit Button */}
              <Button
                type="submit"
                className="btn-primary w-100 py-2 mt-2"
                disabled={isProgress}
              >
                {isProgress && (
                  <img
                    src={Busy}
                    alt="Loading..."
                    className="me-2 spinner-gif"
                  />
                )}
                Create Account
              </Button>

              <p className="mt-3 mb-0 text-center text-muted small">
                Already have an account?{' '}
                <a
                  role="button"
                  onClick={handleSignInRoute}
                  className="fw-semibold"
                >
                  Sign In
                </a>
              </p>
            </Modal.Body>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default SignupForm;
